import { redirect } from '@tanstack/react-router'
import { logout, selectIsAuthenticated, selectUser } from '@/shared/store/auth.store'
import { createDefaultAbility, defineAbilityFor } from './ability'
import type { Action, Subject } from './types'
import { ADMIN_ROLE_NAME } from '@/shared/constants'

/**
 * Build ability synchronously from current auth store snapshot and check permission
 */
export function hasPermissionSync(action: Action, subject: Subject): boolean {
    const user = selectUser()

    if (!user) return false

    if (user.role?.name === ADMIN_ROLE_NAME) return true

    if (user.permissions && user.permissions.length > 0) {
        const ability = defineAbilityFor(user.permissions)
        return ability.can(action, subject)
    }

    if (user.role?.permissions && user.role.permissions.length > 0) {
        const ability = defineAbilityFor(user.role.permissions)
        return ability.can(action, subject)
    }

    // fallback default ability
    const ability = createDefaultAbility()
    return ability.can(action, subject)
}

/**
 * Return a function suitable for TanStack Router's beforeLoad hook
 */
export function requirePermissionBeforeLoad(action: Action, subject: Subject) {
    return async ({ location }: { location: { pathname: string } }) => {
        const isAuth = selectIsAuthenticated()
        const path = location?.pathname || '/'

        if (!isAuth) {
            await logout()
            throw redirect({ to: '/login', search: { redirect: path } })
        }

        const allowed = hasPermissionSync(action, subject)
        if (!allowed) {
            throw redirect({ to: '/not-authorized' })
        }

        return true
    }
}
